import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Clock,
  Calendar,
  Monitor,
  ListVideo,
  Image,
  MoreVertical,
  Trash2,
  Edit,
  Copy,
  Zap,
} from 'lucide-react';
import {
  type Schedule,
  formatTimeRange,
  getDayNames,
  isScheduleActive,
} from '@/hooks/signage';

interface ScheduleCardProps {
  schedule: Schedule;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleActive: (active: boolean) => void;
}

export function ScheduleCard({
  schedule,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleActive,
}: ScheduleCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isCurrentlyActive = isScheduleActive(schedule);
  const timeRange = formatTimeRange(schedule.start_time, schedule.end_time);
  const daysText = getDayNames(schedule.days_of_week);

  const getContentInfo = () => {
    if (schedule.playlist) {
      return {
        icon: <ListVideo className="w-4 h-4" />,
        label: schedule.playlist.name,
        type: 'Playlist',
      };
    }
    if (schedule.content_item) {
      return {
        icon: <Image className="w-4 h-4" />,
        label: schedule.content_item.name,
        type: schedule.content_item.content_type,
      };
    }
    return {
      icon: <Image className="w-4 h-4" />,
      label: 'No content',
      type: 'None',
    };
  };

  const contentInfo = getContentInfo();

  return (
    <>
      <Card className={!schedule.is_active ? 'opacity-60' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base truncate">{schedule.name}</CardTitle>
                {isCurrentlyActive && schedule.is_active && (
                  <Badge variant="default" className="bg-green-500 text-xs">
                    <Zap className="w-3 h-3 mr-1" />
                    Live
                  </Badge>
                )}
              </div>
              <CardDescription className="text-xs mt-1">
                Priority: {schedule.priority}
              </CardDescription>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Schedule
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicate}>
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Schedule
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Time and Days */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{timeRange}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{daysText}</span>
            </div>
          </div>

          {/* Content */}
          <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
            {contentInfo.icon}
            <div className="flex-1 min-w-0">
              <span className="text-sm truncate block">{contentInfo.label}</span>
              <span className="text-xs text-muted-foreground capitalize">
                {contentInfo.type}
              </span>
            </div>
          </div>

          {/* Display target */}
          {schedule.display ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Monitor className="w-4 h-4" />
              <span>{schedule.display.name}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Monitor className="w-4 h-4" />
              <span>All displays</span>
            </div>
          )}

          {/* Date range if set */}
          {(schedule.start_date || schedule.end_date) && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              {schedule.start_date && schedule.end_date
                ? `${schedule.start_date} to ${schedule.end_date}`
                : schedule.start_date
                ? `Starting ${schedule.start_date}`
                : `Until ${schedule.end_date}`}
            </div>
          )}

          {/* Active toggle */}
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-muted-foreground">Active</span>
            <Switch
              checked={schedule.is_active}
              onCheckedChange={onToggleActive}
            />
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{schedule.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
